import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Warehouse as WarehouseIcon } from "lucide-react";

export default function WarehousesPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingWh, setEditingWh] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", address: "", manager_name: "", contact_number: "" });

  const loadWarehouses = async () => {
    try { const { data } = await api.get("/warehouses"); setWarehouses(data); }
    catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };

  useEffect(() => { loadWarehouses(); }, []);

  const openCreate = () => {
    setEditingWh(null);
    setForm({ name: "", location: "", address: "", manager_name: "", contact_number: "" });
    setShowDialog(true);
  };

  const openEdit = (wh) => {
    setEditingWh(wh);
    setForm({ name: wh.name, location: wh.location || "", address: wh.address || "", manager_name: wh.manager_name || "", contact_number: wh.contact_number || "" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingWh) {
        await api.put(`/warehouses/${editingWh._id}`, form);
        toast.success("Warehouse updated");
      } else {
        await api.post("/warehouses", form);
        toast.success("Warehouse created");
      }
      setShowDialog(false);
      loadWarehouses();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this warehouse?")) return;
    try { await api.delete(`/warehouses/${id}`); toast.success("Warehouse deleted"); loadWarehouses(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="warehouses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Warehouses</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage storage locations</p>
        </div>
        <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-warehouse-button">
          <Plus size={14} /> Add Warehouse
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="warehouses-grid">
        {loading ? <p className="text-xs text-zinc-400 col-span-3 text-center py-8">Loading...</p> :
        warehouses.length === 0 ? <p className="text-xs text-zinc-400 col-span-3 text-center py-8">No warehouses yet</p> :
        warehouses.map(wh => (
          <div key={wh._id} className="border border-zinc-200 p-4" data-testid={`warehouse-card-${wh._id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-zinc-100 flex items-center justify-center rounded-sm">
                  <WarehouseIcon size={14} className="text-zinc-500" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-sm text-zinc-900">{wh.name}</h3>
                  <p className="text-[10px] text-zinc-500">{wh.location}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(wh)} className="p-1 hover:bg-zinc-100 rounded-sm"><Edit2 size={12} className="text-zinc-400" /></button>
                <button onClick={() => handleDelete(wh._id)} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={12} className="text-red-400" /></button>
              </div>
            </div>
            <div className="space-y-1 text-xs text-zinc-600">
              <p>{wh.address || 'No address'}</p>
              <p className="text-zinc-400">Manager: {wh.manager_name || '-'} | {wh.contact_number || '-'}</p>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="warehouse-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingWh ? "Edit Warehouse" : "Create Warehouse"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Warehouse Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="warehouse-name-input" /></div>
            <div><Label className="text-xs">Location / City</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1 rounded-sm" data-testid="warehouse-location-input" /></div>
            <div><Label className="text-xs">Full Address</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="mt-1 rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Manager Name</Label><Input value={form.manager_name} onChange={(e) => setForm({...form, manager_name: e.target.value})} className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">Contact Number</Label><Input value={form.contact_number} onChange={(e) => setForm({...form, contact_number: e.target.value})} className="mt-1 rounded-sm" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSave} className="rounded-sm text-xs" data-testid="save-warehouse-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
